// store/chatSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { resolveApiBaseUrl } from '../config/apiConfig';
import { toolsets, introMessage } from '../config/chatConstants';

// ---------- tiny utils ----------
const getEnvApiBase = () => resolveApiBaseUrl();

const trimSlash = (s = '') => s.replace(/\/+$/, '');
const joinUrl = (base, path = '') =>
  `${trimSlash(base)}/${String(path).replace(/^\/+/, '')}`;

const safeParse = (str, fallback) => {
  try { return JSON.parse(str); } catch { return fallback; }
};
const safeStringify = (val, fallback = '') => {
  try { return JSON.stringify(val); } catch { return fallback; }
};
const loadLS = (k, fallback) => {
  try {
    const v = localStorage.getItem(k);
    return v == null ? fallback : safeParse(v, fallback);
  } catch { return fallback; }
};
const saveLS = (k, v) => {
  try { localStorage.setItem(k, safeStringify(v)); } catch { /* ignore */ }
};
const delLS = (k) => {
  try { localStorage.removeItem(k); } catch { /* ignore */ }
};

// ---------- initial messages per tool ----------
const getInitialMessages = () => {
  const initial = {};
  for (const key of Object.keys(toolsets)) {
    initial[key] = loadLS(`togeeChat:${key}`, [introMessage]);
  }
  return initial;
};

// ---------- unify backend shapes to displayable text ----------
function resolveToolResponse(toolKey, data) {
  if (data && typeof data === 'object' && data.creativeOutput) return data.creativeOutput;

  const fb = typeof data === 'string' ? data : safeStringify(data, '');
  const pick = (...keys) => keys.find(k => data && data[k] != null) ? data[keys.find(k => data[k] != null)] : undefined;

  const lookup = {
    chat: pick('message', 'response', 'idea') ?? fb,
    lesson: pick('lessonPlan') ?? fb,
    story: pick('storyProblem', 'story') ?? fb,
    worksheet: pick('worksheet') ?? fb,
    innovation: (data?.idea
      ? `${data.idea}\n\n${data.assessment || ''}\n\n${data.nextSteps || ''}`.trim()
      : fb),
    blog: pick('idea', 'blogInnovation', 'response') ?? fb,
    podcast: pick('response') ?? fb,
  };
  return lookup[toolKey] ?? fb;
}

// ---------- AbortControllers per tool to cancel stale sends ----------
const inflightControllers = new Map(); // key: toolsetKey -> AbortController

// ---------- Thunks ----------
export const fetchLatestChronicle = createAsyncThunk(
  'chat/fetchLatestChronicle',
  async ({ apiBase } = {}, { rejectWithValue }) => {
    const base = trimSlash(apiBase || getEnvApiBase());
    const url = joinUrl(base, '/api/memory/chronicle');
    try {
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status} on ${url}`);
      }
      const data = await res.json().catch(() => null);
      return data?.chronicle ?? data?.data ?? null;
    } catch (e) {
      return rejectWithValue(e.message || String(e));
    }
  }
);

const normalizeHistory = (history = []) =>
  history.map(({ role, content }) => ({
    role: role === 'user' ? 'user' : 'assistant',
    content: typeof content === 'string' ? content : safeStringify(content, String(content)),
  }));

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  /**
   * payload: { prompt, toolsetKey, apiBase?, mode?, historyLimit? }
   */
  async (payload, { rejectWithValue, getState }) => {
    const {
      prompt,
      toolsetKey,
      apiBase,
      mode = 'standard',
      historyLimit = 12
    } = payload || {};

    const tool = toolsets[toolsetKey];
    if (!tool || !tool.endpoint) {
      return rejectWithValue('Invalid toolset selected or missing endpoint.');
    }

    const base = trimSlash(apiBase || getEnvApiBase());
    const url = joinUrl(base, tool.endpoint);

    try {
      const state = getState();
      const byTool = state?.chat?.messagesByToolset || {};
      const history = byTool[toolsetKey] || [];
      const conversationHistory = normalizeHistory(history).slice(-historyLimit);

      // cancel any in-flight for this toolset (prevents race + “stale” responses)
      if (inflightControllers.has(toolsetKey)) {
        try { inflightControllers.get(toolsetKey)?.abort(); } catch { /* ignore */ }
      }
      const controller = new AbortController();
      inflightControllers.set(toolsetKey, controller);

      const body = {
        prompt,
        mode,
        conversationHistory,
        // Inject chronicle if available
        chronicle: state?.chat?.latestChronicle || null
      };
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
        credentials: 'omit',
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status} on ${url}`);
      }

      const data = await res.json().catch(() => ({}));
      const content = resolveToolResponse(toolsetKey, data);

      return {
        role: 'assistant',
        content,
        timestamp: new Date().toISOString(),
        type: toolsetKey,
      };
    } catch (e) {
      if (e.name === 'AbortError') {
        return rejectWithValue('Request canceled (superseded by a new message).');
      }
      return rejectWithValue(e.message || String(e));
    } finally {
      inflightControllers.delete(toolsetKey);
    }
  }
);

// ---------- Slice ----------
const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    messagesByToolset: getInitialMessages(),
    activeToolset: 'chat',
    isLoading: false,
    error: null,
    latestChronicle: null,
    chronicleStatus: 'idle',
    chronicleError: null,
  },
  reducers: {
    setActiveToolset(state, action) {
      state.activeToolset = action.payload;
    },
    addMessage(state, action) {
      const { toolset, message } = action.payload || {};
      if (!toolset) return;
      if (!state.messagesByToolset[toolset]) state.messagesByToolset[toolset] = [introMessage];
      state.messagesByToolset[toolset].push(message);
      saveLS(`togeeChat:${toolset}`, state.messagesByToolset[toolset]);
    },
    clearHistory(state, action) {
      const toolset = action.payload;
      if (!toolset) return;
      state.messagesByToolset[toolset] = [introMessage];
      delLS(`togeeChat:${toolset}`);
    },
  },
  extraReducers: (builder) => {
    builder
      // latest chronicle
      .addCase(fetchLatestChronicle.pending, (state) => {
        state.chronicleStatus = 'loading';
        state.chronicleError = null;
      })
      .addCase(fetchLatestChronicle.fulfilled, (state, action) => {
        state.chronicleStatus = 'succeeded';
        state.latestChronicle = action.payload;
      })
      .addCase(fetchLatestChronicle.rejected, (state, action) => {
        state.chronicleStatus = 'failed';
        state.chronicleError = action.payload || 'Failed to fetch chronicle.';
      })
      // send message
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        const toolset = action.payload?.type || state.activeToolset || 'chat';
        if (!state.messagesByToolset[toolset]) state.messagesByToolset[toolset] = [introMessage];
        state.messagesByToolset[toolset].push(action.payload);
        saveLS(`togeeChat:${toolset}`, state.messagesByToolset[toolset]);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Unknown error';
        const toolset = state.activeToolset || 'chat';
        const msg = {
          role: 'assistant',
          content: `I ran into an error: ${state.error}. Please try again.`,
          timestamp: new Date().toISOString(),
          type: 'error',
        };
        if (!state.messagesByToolset[toolset]) state.messagesByToolset[toolset] = [introMessage];
        state.messagesByToolset[toolset].push(msg);
        saveLS(`togeeChat:${toolset}`, state.messagesByToolset[toolset]);
      });
  },
});

export const { setActiveToolset, addMessage, clearHistory } = chatSlice.actions;

// ---------- Selectors (nice DX) ----------
export const selectActiveToolset = (s) => s.chat.activeToolset;
export const selectMessagesFor = (toolset) => (s) => s.chat.messagesByToolset?.[toolset] || [introMessage];
export const selectIsLoading = (s) => s.chat.isLoading;
export const selectChatError = (s) => s.chat.error;
export const selectLatestChronicle = (s) => s.chat.latestChronicle;
export const selectChronicleStatus = (s) => s.chat.chronicleStatus;
export const selectChronicleError = (s) => s.chat.chronicleError;

export default chatSlice.reducer;
